<script>
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';

export default {
  name: "LinkStaffServiceModal",

  components: {
    BaseModal,
    LoadingState,
    ErrorState,
    EmptyState,
    PaginationBar,
  },

  props: {
    services: {
      type: Array,
      default: () => [],
    },
    staffId: {
      type: String,
      required: true,
    },
    branchId: {
      type: String,
      required: true,
    }
  },

  data() {
    return {
      initialSelectedIds: [],
      selectedServiceIds: [],
      selectedIdsMap: {},
      isConfirming: false,
      isAllLoading: false,
      debouncedUpdateQuery: null,
      hasInitialized: false,
    };
  },

  created() {
    this.debouncedUpdateQuery = debounce((value) => {
      this.$store.dispatch('serviceOptions/updateFiltersAndFetch', { q: value });
    }, 300);
  },

  computed: {
    isLoading() {
      return this.$store.state.serviceOptions.isLoading || false;
    },
    isError() {
      return this.$store.state.serviceOptions.isError || false;
    },
    optionList() {
      return this.$store.state.serviceOptions.options || [];
    },
    pagination() {
      return this.$store.getters['serviceOptions/formattedPagination'];
    },
    filters() {
      return this.$store.state.serviceOptions.filters;
    },
    filterQuery: {
      get() { return this.filters.q; },
      set(value) { this.debouncedUpdateQuery(value); }
    },
    isFilterActive() {
      return this.filters.branch_id || this.filters.q;
    },
    emptyStateMessage() {
      if (this.isFilterActive) return 'No services match your current filters.';
      return 'Try creating a new service to get started.';
    },
    currentPageIds() {
      return this.optionList.map(item => item.service.id);
    },
    allSelectedPage() {
      return this.currentPageIds.every(id => this.selectedIdsMap[id]);
    },
    selectedCount() {
      return Object.values(this.selectedIdsMap).filter(Boolean).length;
    },
  },

  methods: {
    /** Pre-fill selection from parent */
    fetchExistingServices() {
      this.initialSelectedIds = this.services.map(s => s.id);
      this.initialSelectedIds.forEach(id => {
        this.$set(this.selectedIdsMap, id, true);
      });
      this.emitSelection();
    },

    async fetchOptions() {
      try {
        await this.$store.dispatch('serviceOptions/fetchOptions');
      } catch(err) {
        useAlert(err);
        console.error(err);
      }
    },

    async fetchOptionsByBranch(branchId) {
      try {
        await this.$store.dispatch('serviceOptions/updateFiltersAndFetch', { 
          branch_id: branchId,
          enabled: null 
        });
      } catch(err) {
        useAlert(err);
        console.error(err);
      }
    },

    fetchPreviousPage() {
      if (this.pagination.currentPage > 1) {
        let prev = this.pagination.currentPage - 1;
        this.$store.dispatch('serviceOptions/updatePaginationAndFetch', { page: prev });
      }
    },

    fetchNextPage() {
      if (this.pagination.currentPage < this.pagination.totalPages) {
        let next = this.pagination.currentPage + 1;
        this.$store.dispatch('serviceOptions/updatePaginationAndFetch', { page: next });
      }
    },

    updatePageSize(newSize) {
      this.$store.dispatch('serviceOptions/updatePaginationAndFetch', { page: 1, page_size: newSize });
    },

    async clearFilters() {
      await this.$store.dispatch('serviceOptions/reset');
      await this.$store.dispatch('serviceOptions/updateFiltersAndFetch', { 
        branch_id: this.branchId,
        enabled: null,
        q: null
      });
    },

    toggleSelect(serviceId) {
      this.$set(this.selectedIdsMap, serviceId, !this.selectedIdsMap[serviceId]);
      this.emitSelection();
    },

    toggleSelectAllPage() {
      const newValue = !this.allSelectedPage;
      this.currentPageIds.forEach(id => {
        this.$set(this.selectedIdsMap, id, newValue);
      });
      this.emitSelection();
    },

    emitSelection() {
      this.selectedServiceIds = Object.keys(this.selectedIdsMap).filter(id => this.selectedIdsMap[id]);
    },

    async handleConfirm() {
      try {
        this.isConfirming = true;

        await this.$store.dispatch('serviceStaff/bulkAssignServicesToStaff', { 
          branch_id: this.branchId,
          staff_id: this.staffId,
          service_ids: this.selectedServiceIds,
          update_mode: 'replace',
        });

        useAlert(`Services linked to staff`);
        this.$store.dispatch('onboarding/completeLocalTask', 'link_service_to_staff');
        if(this.$route.query.onboarding === 'true') {
          this.$router.push(this.$route.path);
        }
        this.$emit('refresh');
        this.$emit('close');

      } catch (err) {
        console.error(err);
        useAlert(`Failed to link services to staff: ${err}`);
      } finally {
        this.isConfirming = false;
      }
    },
  },

  watch: {
    optionList: {
      handler() {
        // Only apply initial selections once, when the component first loads
        if (!this.hasInitialized && this.optionList.length > 0) {
          this.initialSelectedIds.forEach(id => {
            if (!(id in this.selectedIdsMap)) {
              this.$set(this.selectedIdsMap, id, true);
            }
          });
          this.hasInitialized = true;
          this.emitSelection();
        }
      },
      immediate: true
    }
  },

  async mounted() {
    this.isAllLoading = true;
    await this.$store.dispatch('serviceOptions/reset');
    this.fetchExistingServices();
    await this.fetchOptionsByBranch(this.branchId);
    this.isAllLoading = false;
  }
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      Link Services to Staff
    </template>

    <div class="w-full">
      <!-- Filters -->
      <div class="w-full flex gap-4 items-end mb-4">
        <input 
          v-model="filterQuery" 
          placeholder="Enter your query here..."
          class="border px-2 py-1.5 border-slate-200 rounded-md flex-1"
        />
        <woot-button color-scheme="secondary" @click="clearFilters" :disabled="!isFilterActive">
          Clear Filters
        </woot-button>
      </div>

      <!-- Pagination -->
      <PaginationBar
        v-if="!isLoading && !isError && optionList.length !== 0"
        :pagination="pagination"
        :loading="isLoading"
        @previous="fetchPreviousPage"
        @next="fetchNextPage"
        @page-size-change="updatePageSize"
      />

      <div class="relative">
        <!-- Table -->
        <div v-if="!isLoading && !isError && optionList.length">
          <table class="w-full border-collapse border border-slate-200 dark:border-slate-700 text-left">
            <thead>
              <tr class="bg-slate-100 dark:bg-slate-800">
                <th class="p-2 border border-slate-200 dark:border-slate-700" style="width: 48px; max-width: 48px; text-align: center;">
                  <input type="checkbox" :checked="allSelectedPage" @change="toggleSelectAllPage" />
                </th>
                <th class="p-2 border border-slate-200 dark:border-slate-700">Service Name</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in optionList"
                :key="item.service.id"
                class="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                @click="toggleSelect(item.service.id)"
              >
                <td class="p-2 border border-slate-200 dark:border-slate-700" style="width: 48px; max-width: 48px; text-align: center;">
                  <input
                    type="checkbox"
                    :checked="selectedIdsMap[item.service.id]"
                    @click.stop
                    @change="toggleSelect(item.service.id)"
                  />
                </td>
                <td class="p-2 border border-slate-200 dark:border-slate-700">
                  {{ item.service.name }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="!isLoading && !isError && optionList.length" class="flex justify-end gap-4 mt-2">
          <p class="text-sm text-slate-600">
            <span class="font-semibold">{{ selectedCount }}</span> selected
          </p>
        </div>

        <!-- Loading/Error/Empty States -->
        <LoadingState v-if="isLoading" message="Loading services..." />
        <ErrorState v-else-if="!isLoading && isError" />
        <EmptyState
          v-else-if="!isLoading && !isError && optionList.length === 0"
          title="No Service Found"
          :message="emptyStateMessage"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <woot-button
          color-scheme="secondary"
          @click="$emit('close')"
          :disabled="isAllLoading"
        >
          Cancel
        </woot-button>

        <woot-button
          @click="handleConfirm"
          :is-loading="isConfirming"
          :disabled="isAllLoading"
        >
          Link
        </woot-button>
      </div>
    </template>
  </BaseModal>
</template>
